(() => {
  class FoxtrotSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="q"]');
      this.panel = this.querySelector('.fg-search-panel');
      this.results = this.querySelector('[data-fg-search-results]');
      this.message = this.querySelector('[data-fg-search-message]');
      this.status = this.querySelector('[data-fg-search-status]');
      this.all = this.querySelector('[data-fg-search-all]');
      if (!this.form || !this.input || !this.panel || !this.results || !this.message || !this.status || !this.all) {
        console.error('Foxtrot search is missing required markup.', this);
        return;
      }
      this.events?.abort();
      this.cancel();
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.results.replaceChildren();
      this.all.hidden = true;
      this.status.textContent = '';
      this.loadedQuery = null;
      this.isComposing = false;
      this.prompt = this.dataset.prompt;
      this.message.hidden = false;
      this.message.textContent = this.prompt;
      for (const attribute of ['role', 'aria-autocomplete', 'aria-haspopup', 'aria-controls', 'aria-expanded']) {
        this.input.removeAttribute(attribute);
      }
      const trigger = this.form.querySelector('button');
      trigger.type = 'submit';
      if (this.dataset.enabled !== 'true') return;
      this.types = this.dataset.types.split(',').filter(Boolean);
      this.limit = Number(this.dataset.limit);
      if (!this.types.length) return;
      if (this.types.some((type) => !['product', 'collection', 'page', 'article'].includes(type)) ||
          !Number.isInteger(this.limit) || this.limit < 2 || this.limit > 6) {
        console.error('Foxtrot predictive search configuration is invalid; full search remains available.', this);
        return;
      }
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'dialog');
      this.input.setAttribute('aria-controls', this.panel.id);
      this.input.setAttribute('aria-expanded', 'false');
      trigger.type = 'button';
      trigger.addEventListener('click', () => {
        this.input.focus();
        this.open();
      }, options);
      this.input.addEventListener('focus', () => this.open(), options);
      this.input.addEventListener('click', () => this.open(), options);
      this.input.addEventListener('input', (event) => {
        if (!event.isComposing) this.search();
      }, options);
      this.input.addEventListener('compositionstart', () => {
        this.isComposing = true;
        this.cancel();
      }, options);
      this.input.addEventListener('compositionend', () => {
        this.isComposing = false;
        this.search();
      }, options);
      this.form.addEventListener('submit', (event) => {
        if (this.isComposing) event.preventDefault();
        else this.close();
      }, options);
      this.querySelector('[data-fg-search-close]').addEventListener('click', () => this.close(true), options);
      this.addEventListener('keydown', (event) => this.keydown(event), options);
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        }, 0);
      }, options);
      this.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('a')) this.close();
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.close();
      }, options);
      window.addEventListener('resize', () => this.sizePanel(), options);
      window.addEventListener('scroll', () => this.sizePanel(), { ...options, passive: true });
      window.visualViewport?.addEventListener('resize', () => this.sizePanel(), options);
      window.visualViewport?.addEventListener('scroll', () => this.sizePanel(), options);
      if (this.contains(document.activeElement)) this.open();
    }

    disconnectedCallback() {
      if (this.events) this.close();
      else this.cancel();
      this.events?.abort();
    }

    cancel() {
      clearTimeout(this.timer);
      clearTimeout(this.focusTimer);
      this.request?.abort();
      this.revision = (this.revision || 0) + 1;
      this.panel?.removeAttribute('aria-busy');
    }

    open() {
      if (!this.hasAttribute('open')) {
        document.querySelectorAll('foxtrot-search[open]').forEach((other) => {
          if (other !== this) other.close();
        });
        this.setAttribute('open', '');
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.dispatchEvent(new CustomEvent('foxtrot:search:open', { bubbles: true }));
      }
      this.sizePanel();
      const query = this.input.value.trim();
      if (query.length >= 2 && query !== this.loadedQuery && !this.panel.hasAttribute('aria-busy')) this.search();
    }

    close(restoreFocus = false) {
      if (restoreFocus) this.input.focus({ preventScroll: true });
      this.cancel();
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.status.textContent = '';
    }

    sizePanel() {
      if (!this.hasAttribute('open')) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : innerHeight;
      this.panel.style.setProperty('--fg-search-height', `${Math.max(100, bottom - this.panel.getBoundingClientRect().top - 12)}px`);
    }

    search() {
      this.cancel();
      const query = this.input.value.trim();
      this.loadedQuery = null;
      this.results.replaceChildren();
      this.all.hidden = query.length < 2;
      const searchUrl = new URL(this.form.action);
      searchUrl.searchParams.set('q', query);
      searchUrl.searchParams.set('options[prefix]', 'last');
      this.all.href = searchUrl.href;
      this.message.hidden = false;
      this.message.textContent = query.length < 2 ? this.prompt : this.dataset.loading;
      this.status.textContent = this.message.textContent;
      if (!this.hasAttribute('open')) {
        this.setAttribute('open', '');
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.dispatchEvent(new CustomEvent('foxtrot:search:open', { bubbles: true }));
      }
      this.sizePanel();
      if (query.length < 2) return;
      this.panel.setAttribute('aria-busy', 'true');
      const revision = this.revision;
      this.timer = setTimeout(() => this.fetchResults(query, revision), 250);
    }

    async fetchResults(query, revision) {
      const request = new AbortController();
      this.request = request;
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        request.abort();
      }, 8000);
      try {
        const url = new URL(this.dataset.endpoint, location.origin);
        url.search = new URLSearchParams({
          q: query,
          section_id: 'foxtrot-predictive-search',
          'resources[type]': this.types.join(','),
          'resources[limit]': String(this.limit),
          'resources[limit_scope]': 'each'
        }).toString();
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Predictive search returned HTTP ${response.status}.`);
        const html = await response.text();
        if (revision !== this.revision || !this.isConnected || !this.hasAttribute('open')) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-fg-predictive-response]');
        if (!content) throw new Error('Predictive search response is missing its results.');
        this.results.replaceChildren(content);
        this.loadedQuery = query;
        this.message.hidden = true;
        const count = this.results.querySelectorAll('[data-fg-search-result]').length;
        this.status.textContent = count ? this.dataset.count.replace('[count]', String(count)) : this.results.textContent.trim();
      } catch (error) {
        if (revision !== this.revision || (!timedOut && error.name === 'AbortError')) return;
        console.error('Foxtrot predictive search failed; full search remains available.', error);
        this.results.replaceChildren();
        this.message.hidden = false;
        this.message.textContent = this.dataset.error;
        this.status.textContent = this.dataset.error;
      } finally {
        clearTimeout(timeout);
        if (revision === this.revision) this.panel.removeAttribute('aria-busy');
      }
    }

    keydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        if (!this.hasAttribute('open')) return;
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (event.target !== this.input && !(event.target instanceof Element && event.target.closest('a'))) return;
        event.preventDefault();
        this.open();
        const links = Array.from(this.panel.querySelectorAll('a[href]')).filter((link) => !link.hidden);
        if (!links.length) return;
        const current = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? current + 1 : (current < 0 ? links.length - 1 : current - 1);
        if (next < 0 || next >= links.length) this.input.focus({ preventScroll: true });
        else links[next].focus({ preventScroll: true });
        if (next >= 0 && next < links.length) links[next].scrollIntoView({ block: 'nearest' });
      }
    }
  }

  if (!customElements.get('foxtrot-search')) customElements.define('foxtrot-search', FoxtrotSearch);
})();
